import {
  $,
  appState,
  fileTransfers,
  api,
  MAX_WEB_FILE_COUNT,
  MAX_WEB_FILE_BYTES,
  escapeHtml,
  formatFileSize,
  fileTransferPercent,
  fileTransferStateLabel,
  formatTransferRate,
  formatTransferEta,
  type WebFileTransferEntry,
} from './app-state';
import {
  createFileTransferId,
  isFileTransferProgressStale,
  shouldPersistFileTransferProgress,
  updateFileTransferRate,
  type FileTransferPersistMarker,
  type FileTransferProgress,
} from '../shared/filexfer';
import { fingerprintFileSource } from '../shared/file-source-fingerprint';
import {
  loadFileTransferTasks,
  normalizeFileTransferTasks,
  removeFileTransferTask,
  saveFileTransferTasks,
  upsertFileTransferTask,
  type PersistedFileSendTask,
  type RemoteFileTransferTask,
} from '../shared/file-transfer-tasks';

// 当前页面仍保留 File 对象时可以直接重试；页面重载后只保留任务元数据，
// 需要用户重新选择同一文件，避免后台读取或上传用户未确认的内容。
const fileTransferSources = new Map<string, File>();
let fileTransferRateRefreshTimer: number | null = null;
const taskPersistMarkers = new Map<string, FileTransferPersistMarker>();
function readPersistedTasks(): PersistedFileSendTask[] {
  try {
    return loadFileTransferTasks(window.localStorage);
  } catch {
    return [];
  }
}

let persistedTasks: PersistedFileSendTask[] = readPersistedTasks();
let pendingSourceSelectionId: string | null = null;
// 服务端同步是 best-effort 的，且按任务合并请求，避免每个 16KB 分块都
// 触发一次 REST 请求。操作按任务串行，防止“完成后删除”被旧的进度写回覆盖。
const serverTaskSyncTimers = new Map<string, number>();
const serverTaskSyncPending = new Map<string, PersistedFileSendTask>();
const serverTaskOperations = new Map<string, Promise<void>>();

function savePersistedTasks(): void {
  try {
    saveFileTransferTasks(window.localStorage, persistedTasks);
  } catch {
    // 隐私模式可能连 localStorage 属性本身也不可访问。
  }
}

function currentTargetDeviceId(): string | undefined {
  const value = appState.controller?.getConnectionDiagnostics().peerDeviceId;
  return typeof value === 'string' && value ? value : undefined;
}

function enqueueServerTaskOperation(taskId: string, operation: () => Promise<void>): void {
  const previous = serverTaskOperations.get(taskId) || Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(operation)
    .finally(() => {
      if (serverTaskOperations.get(taskId) === next) serverTaskOperations.delete(taskId);
    });
  serverTaskOperations.set(taskId, next);
}

function queueServerTaskSync(task: PersistedFileSendTask): void {
  if (!api.token) return;
  serverTaskSyncPending.set(task.id, task);
  if (serverTaskSyncTimers.has(task.id)) return;
  const timer = window.setTimeout(() => {
    serverTaskSyncTimers.delete(task.id);
    const latest = serverTaskSyncPending.get(task.id);
    serverTaskSyncPending.delete(task.id);
    if (!latest || !api.token) return;
    enqueueServerTaskOperation(task.id, async () => {
      if (!api.token) return;
      await api.upsertFileTransferTask({
        id: latest.id,
        name: latest.name,
        size: latest.size,
        transferred: latest.transferred,
        ...(latest.sourceFingerprint ? { sourceFingerprint: latest.sourceFingerprint } : {}),
        ...(latest.targetDeviceId ? { targetDeviceId: latest.targetDeviceId } : {}),
        state: latest.error ? 'failed' : 'transferring',
        ...(latest.error ? { error: latest.error } : {}),
      });
    });
  }, 800);
  serverTaskSyncTimers.set(task.id, timer);
}

function queueServerTaskDelete(taskId: string): void {
  serverTaskSyncPending.delete(taskId);
  const timer = serverTaskSyncTimers.get(taskId);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    serverTaskSyncTimers.delete(taskId);
  }
  if (!api.token) return;
  enqueueServerTaskOperation(taskId, async () => {
    if (api.token) await api.deleteFileTransferTask(taskId);
  });
}

function rememberSendTask(
  progress: Pick<FileTransferProgress, 'id' | 'name' | 'size' | 'transferred' | 'error'> & {
    sourceFingerprint?: string;
  },
): void {
  persistedTasks = upsertFileTransferTask(persistedTasks, {
    id: progress.id,
    name: progress.name,
    size: progress.size,
    transferred: progress.transferred,
    ...(progress.sourceFingerprint ? { sourceFingerprint: progress.sourceFingerprint } : {}),
    targetDeviceId: currentTargetDeviceId(),
    ...(progress.error ? { error: progress.error } : {}),
  });
  savePersistedTasks();
  taskPersistMarkers.set(progress.id, { transferred: progress.transferred, at: Date.now() });
  queueServerTaskSync(persistedTasks.find((task) => task.id === progress.id)!);
}

function forgetSendTask(id: string): void {
  taskPersistMarkers.delete(id);
  const next = removeFileTransferTask(persistedTasks, id);
  if (next.length === persistedTasks.length) return;
  persistedTasks = next;
  savePersistedTasks();
  queueServerTaskDelete(id);
}

function restorePersistedFileTasks(): void {
  persistedTasks = readPersistedTasks();
  for (const task of persistedTasks) {
    if (fileTransfers.has(task.id)) continue;
    fileTransfers.set(task.id, {
      id: task.id,
      name: task.name,
      size: task.size,
      transferred: task.transferred,
      direction: 'send',
      state: 'failed',
      error: task.error || '上次传输未完成，请重新选择同一文件',
      recoverable: true,
      updatedAt: task.updatedAt,
      speedBps: 0,
      lastProgressAt: task.updatedAt,
      sampleAt: task.updatedAt,
      sampleBytes: task.transferred,
    });
  }
}

function persistedTaskFromRemote(task: RemoteFileTransferTask): PersistedFileSendTask | null {
  if (task.state !== 'transferring' && task.state !== 'failed') return null;
  const normalized = normalizeFileTransferTasks([{
    version: 1,
    id: task.id,
    name: task.name,
    size: task.size,
    transferred: task.transferred,
    ...(task.sourceFingerprint ? { sourceFingerprint: task.sourceFingerprint } : {}),
    ...(task.targetDeviceId ? { targetDeviceId: task.targetDeviceId } : {}),
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    ...(task.error ? { error: task.error } : {}),
  }]);
  return normalized[0] || null;
}

// 登录后从服务端补齐任务列表。它只负责“恢复提示”和元数据合并，
// 不会自动读取用户文件，也不会自动重新发送，必须由用户重新选择文件。
async function syncPersistedFileTasks(): Promise<void> {
  if (!api.token) return;
  const result = await api.listFileTransferTasks();
  if (!result.ok) return;
  let changed = false;
  for (const remote of result.tasks) {
    const task = persistedTaskFromRemote(remote);
    if (!task) continue;
    // 页面当前正在发送时，以 RTC 实时进度为准，避免后台同步把进度回拨。
    if (fileTransfers.get(task.id)?.state === 'transferring') continue;
    const local = persistedTasks.find((item) => item.id === task.id);
    if (local && local.updatedAt > task.updatedAt) continue;
    persistedTasks = upsertFileTransferTask(persistedTasks, task);
    changed = true;
  }
  if (!changed) return;
  savePersistedTasks();
  restorePersistedFileTasks();
  renderFileTransfers();
}

function updateFileTransferCenter(rows: WebFileTransferEntry[]): void {
  const center = $('file-transfer-center');
  const panel = $('file-transfers');
  const toggle = $('file-transfers-toggle') as HTMLButtonElement;
  const count = $('file-transfer-count');
  const summary = $('file-transfer-summary');
  const overview = $('file-transfer-overview');
  const sectionCount = $('file-transfer-section-count');
  const activeCount = rows.filter((row) => row.state === 'transferring').length;
  const failedCount = rows.filter((row) => row.state === 'failed').length;
  const completedCount = rows.filter((row) => row.state === 'completed').length;
  const cancelledCount = rows.filter((row) => row.state === 'cancelled').length;

  if (rows.length === 0) appState.fileTransferPanelOpen = false;
  const hasRows = rows.length > 0;
  const statusText = activeCount > 0
      ? `${activeCount} 个进行中`
      : failedCount > 0
        ? `${failedCount} 个失败`
        : completedCount > 0
          ? `${completedCount} 个已完成`
          : `${cancelledCount} 个已取消`;

  center.hidden = !hasRows;
  center.dataset.state = activeCount > 0 ? 'active' : failedCount > 0 ? 'failed' : 'settled';
  center.dataset.open = String(appState.fileTransferPanelOpen && hasRows);
  panel.classList.toggle('is-open', appState.fileTransferPanelOpen && hasRows);
  panel.setAttribute('aria-hidden', String(!(appState.fileTransferPanelOpen && hasRows)));
  toggle.setAttribute('aria-expanded', String(appState.fileTransferPanelOpen && hasRows));
  toggle.setAttribute('aria-label', appState.fileTransferPanelOpen && hasRows
    ? '收起文件传输中心'
    : `打开文件传输中心，${statusText}`);
  count.textContent = String(activeCount || rows.length);
  summary.textContent = statusText;
  overview.textContent = activeCount > 0
    ? `${activeCount} 个任务正在传输`
    : `${rows.length} 个传输记录`;
  sectionCount.textContent = `${rows.length} 项`;
}

function closeFileTransfers(restoreFocus = true): void {
  if (!appState.fileTransferPanelOpen) return;
  appState.fileTransferPanelOpen = false;
  updateFileTransferCenter(Array.from(fileTransfers.values()));
  if (restoreFocus) $('file-transfers-toggle').focus({ preventScroll: true });
}

function toggleFileTransfers(): void {
  if (fileTransfers.size === 0) return;
  appState.fileTransferPanelOpen = !appState.fileTransferPanelOpen;
  updateFileTransferCenter(Array.from(fileTransfers.values()));
}

function renderFileTransfers(): void {
  const list = $('file-transfer-list');
  const clearButton = $('file-transfers-clear') as HTMLButtonElement;
  const rows = Array.from(fileTransfers.values()).sort((a, b) => b.updatedAt - a.updatedAt);
  clearButton.disabled = !rows.some((row) => row.state !== 'transferring');
  list.innerHTML = rows.map((row) => {
    const stale = row.state === 'transferring' && isFileTransferProgressStale(row, Date.now());
    const percent = fileTransferPercent(row);
    const direction = row.direction === 'send' ? '发送' : '接收';
    const stateLabel = fileTransferStateLabel(row);
    const detail = row.state === 'transferring'
      ? `${percent.toFixed(0)}% · ${formatFileSize(row.transferred)} / ${formatFileSize(row.size)}`
      : stateLabel;
    const rate = row.state === 'transferring'
      ? stale ? '等待数据…' : formatTransferRate(row.speedBps)
      : row.state === 'completed'
        ? `${formatFileSize(row.size)} · ${stateLabel}`
        : stateLabel;
    const eta = row.state === 'transferring'
      ? stale ? '等待数据…' : formatTransferEta(row, row.speedBps)
      : '';
    const secondary = row.state === 'transferring'
      ? row.transferred >= row.size
        ? eta
        : stale ? '等待接收数据恢复…' : `预计剩余 ${eta}`
      : row.state === 'completed'
        ? formatFileSize(row.size)
      : row.state === 'cancelled'
          ? '已取消，可重新发送'
        : row.recoverable
          ? '请重新选择同一文件继续发送'
          : '请检查连接后重试';
    const error = (row.state === 'failed' || row.state === 'cancelled') && row.error
      ? `<span class="file-transfer-error">${escapeHtml(row.error)}</span>`
      : '';
    const stateIcon = row.state === 'completed'
      ? 'check'
      : row.state === 'failed'
        ? 'alert'
        : row.state === 'cancelled'
          ? 'close'
          : row.direction === 'send'
            ? 'upload'
            : 'download';
    const cancel = row.state === 'transferring'
      ? `<button class="file-transfer-cancel" type="button" data-file-cancel="${escapeHtml(row.id)}">取消</button>`
      : '';
    const canRetry = row.direction === 'send' && (row.state === 'failed' || row.state === 'cancelled') &&
      (fileTransferSources.has(row.id) || row.recoverable);
    const retry = canRetry
      ? `<button class="file-transfer-retry" type="button" data-file-retry="${escapeHtml(row.id)}">${fileTransferSources.has(row.id) ? '重试' : '选择文件恢复'}</button>`
      : '';
    return (
      `<li class="file-transfer-item" data-state="${row.state}" data-direction="${row.direction}">` +
      `<div class="file-transfer-item-main"><span class="file-transfer-state-icon" aria-hidden="true"><svg class="ic"><use href="#icon-${stateIcon}"></use></svg></span><div class="file-transfer-item-copy">` +
      `<div class="file-transfer-row"><span class="file-transfer-name" title="${escapeHtml(row.name)}">${escapeHtml(row.name)}</span>` +
      `<span class="file-transfer-direction">${direction}</span></div>` +
      `<div class="file-transfer-meta"><span>${escapeHtml(detail)}</span><span class="file-transfer-rate">${escapeHtml(rate)}</span></div>` +
      `<progress class="file-transfer-progress" max="100" value="${percent}" aria-label="${escapeHtml(`${row.name} ${direction}`)}" aria-valuetext="${escapeHtml(detail)}"></progress>` +
      `<div class="file-transfer-meta file-transfer-meta-secondary"><span>${escapeHtml(secondary)}</span>${error}${retry}${cancel}</div>` +
      `</div></div>` +
      `</li>`
    );
  }).join('');
  updateFileTransferCenter(rows);
}

function scheduleFileTransferRender(): void {
  if (appState.fileTransferRenderTimer !== null) return;
  appState.fileTransferRenderTimer = window.setTimeout(() => {
    appState.fileTransferRenderTimer = null;
    renderFileTransfers();
  }, 80);
}

function scheduleFileTransferRateRefresh(): void {
  if (fileTransferRateRefreshTimer !== null) return;
  fileTransferRateRefreshTimer = window.setTimeout(() => {
    fileTransferRateRefreshTimer = null;
    if (![...fileTransfers.values()].some((row) => row.state === 'transferring')) return;
    renderFileTransfers();
    scheduleFileTransferRateRefresh();
  }, 250);
}

function announceFileTransfer(progress: FileTransferProgress): void {
  const announcement = $('file-transfer-announcement');
  if (progress.state === 'transferring') {
    announcement.textContent = `${progress.direction === 'send' ? '开始发送' : '开始接收'}文件 ${progress.name}`;
  } else if (progress.state === 'completed') {
    announcement.textContent = `${progress.direction === 'send' ? '文件已发送' : '文件已接收'}：${progress.name}`;
  } else if (progress.state === 'cancelled') {
    announcement.textContent = `文件传输已取消：${progress.name}`;
  } else {
    announcement.textContent = `文件传输失败：${progress.name}。${progress.error || ''}`;
  }
}

function onFileTransfer(progress: FileTransferProgress): void {
  const previous = fileTransfers.get(progress.id);
  const updatedAt = Date.now();
  const rate = updateFileTransferRate(previous, progress, updatedAt);
  fileTransfers.set(progress.id, { ...progress, updatedAt, ...rate });
  if (progress.direction === 'send') {
    if (progress.state === 'completed' || progress.state === 'cancelled') {
      fileTransferSources.delete(progress.id);
      forgetSendTask(progress.id);
    } else if (shouldPersistFileTransferProgress(progress, taskPersistMarkers.get(progress.id))) {
      rememberSendTask(progress);
    }
  }
  if (!previous || previous.state !== progress.state && progress.state !== 'transferring') {
    announceFileTransfer(progress);
  }
  scheduleFileTransferRender();
  scheduleFileTransferRateRefresh();
}

function clearFinishedFileTransfers(): void {
  for (const [id, progress] of fileTransfers) {
    if (progress.state !== 'transferring') {
      fileTransfers.delete(id);
      fileTransferSources.delete(id);
      forgetSendTask(id);
    }
  }
  renderFileTransfers();
}

function resetFileTransfers(): void {
  for (const progress of fileTransfers.values()) {
    if (progress.direction === 'send' && progress.state === 'transferring') {
      rememberSendTask(progress);
    }
  }
  appState.fileTransferPanelOpen = false;
  fileTransfers.clear();
  pendingSourceSelectionId = null;
  if (appState.fileTransferRenderTimer !== null) {
    window.clearTimeout(appState.fileTransferRenderTimer);
    appState.fileTransferRenderTimer = null;
  }
  restorePersistedFileTasks();
  renderFileTransfers();
  $('file-transfer-announcement').textContent = '';
}

function updateFileTransferButton(): void {
  const button = $('tb-file') as HTMLButtonElement;
  const label = button.querySelector('span');
  button.disabled = appState.fileTransferBusy;
  button.classList.toggle('active', appState.fileTransferBusy);
  button.title = appState.fileTransferBusy ? '文件发送中…' : '选择文件发送到远端';
  if (label) label.textContent = appState.fileTransferBusy ? '发送中…' : '文件';
}

function setFileDropActive(active: boolean): void {
  const stage = $('stage');
  const overlay = $('file-drop-overlay');
  stage.classList.toggle('file-drop-active', active);
  overlay.classList.toggle('hidden', !active);
}

async function sendWebFiles(files: File[]): Promise<void> {
  if (appState.sessionMode === 'view') return;
  if (!appState.controller || appState.fileTransferBusy || files.length === 0) return;

  if (files.length > MAX_WEB_FILE_COUNT) {
    $('session-status').textContent = `一次最多发送 ${MAX_WEB_FILE_COUNT} 个文件`;
    return;
  }
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  if (totalBytes > MAX_WEB_FILE_BYTES) {
    $('session-status').textContent = `本次文件总大小不能超过 ${formatFileSize(MAX_WEB_FILE_BYTES)}`;
    return;
  }

  appState.fileTransferBusy = true;
  updateFileTransferButton();
  try {
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const transferId = createFileTransferId();
      fileTransferSources.set(transferId, file);
      const sourceFingerprint = await fingerprintFileSource(file);
      rememberSendTask({ id: transferId, name: file.name, size: file.size, transferred: 0, sourceFingerprint });
      $('session-status').textContent = `正在发送 ${index + 1}/${files.length} · ${file.name}（${formatFileSize(file.size)}）`;
      await appState.controller.sendFile(file, transferId);
    }
    $('session-status').textContent = files.length === 1
      ? `文件已发送：${files[0].name}`
      : `已发送 ${files.length} 个文件到远端`;
  } catch (error) {
    $('session-status').textContent = error instanceof Error ? error.message : '文件发送失败，请重试';
  } finally {
    appState.fileTransferBusy = false;
    updateFileTransferButton();
  }
}

async function retryFileTransfer(id: string, selectedFile?: File): Promise<void> {
  if (appState.sessionMode === 'view' || appState.fileTransferBusy) return;
  const task = persistedTasks.find((item) => item.id === id);
  const file = selectedFile || fileTransferSources.get(id);
  const controller = appState.controller;
  if (!file || !controller) return;
  if (task && (file.name !== task.name || file.size !== task.size)) {
    onFileTransfer({
      id,
      name: task.name,
      size: task.size,
      transferred: task.transferred,
      direction: 'send',
      state: 'failed',
      recoverable: true,
      error: '文件名称或大小不一致，请选择原文件',
    });
    $('session-status').textContent = '请选择名称和大小都一致的原文件继续发送';
    return;
  }
  const sourceFingerprint = await fingerprintFileSource(file);
  if (task?.sourceFingerprint && sourceFingerprint !== task.sourceFingerprint) {
    onFileTransfer({
      id,
      name: task.name,
      size: task.size,
      transferred: task.transferred,
      direction: 'send',
      state: 'failed',
      recoverable: true,
      error: '文件内容可能已变化，请选择原文件',
    });
    $('session-status').textContent = '文件内容与上次任务不一致，请选择原文件继续发送';
    return;
  }
  if (task?.targetDeviceId) {
    const current = currentTargetDeviceId();
    if (current && current !== task.targetDeviceId) {
      $('session-status').textContent = '请先连接发起任务的原设备，再恢复文件传输';
      return;
    }
  }
  fileTransferSources.set(id, file);
  rememberSendTask({
    id,
    name: file.name,
    size: file.size,
    transferred: task?.transferred || 0,
    sourceFingerprint,
  });
  appState.fileTransferBusy = true;
  updateFileTransferButton();
  $('session-status').textContent = `正在重试 ${file.name}…`;
  try {
    await controller.sendFile(file, id);
    $('session-status').textContent = `文件已发送：${file.name}`;
  } catch (error) {
    $('session-status').textContent = error instanceof Error ? error.message : '文件发送失败，请重试';
  } finally {
    appState.fileTransferBusy = false;
    updateFileTransferButton();
  }
}

function requestFileTransferSource(id: string): void {
  if (!persistedTasks.some((task) => task.id === id) && !fileTransferSources.has(id)) return;
  pendingSourceSelectionId = id;
}

function takeFileTransferSourceSelection(): string | null {
  const id = pendingSourceSelectionId;
  pendingSourceSelectionId = null;
  return id;
}

export {
  updateFileTransferCenter,
  closeFileTransfers,
  toggleFileTransfers,
  renderFileTransfers,
  scheduleFileTransferRender,
  onFileTransfer,
  clearFinishedFileTransfers,
  resetFileTransfers,
  updateFileTransferButton,
  setFileDropActive,
  sendWebFiles,
  retryFileTransfer,
  requestFileTransferSource,
  takeFileTransferSourceSelection,
  restorePersistedFileTasks,
  syncPersistedFileTasks,
};
